import DeviceDetector from 'device-detector-js'
import { v5 as uuidv5 } from 'uuid'
import { load as loadFingerprint } from '@fingerprintjs/fingerprintjs'
import { UserDevice } from '~/lib/services/im/devices.dto'
import { isCypressSpecialBehaviour } from '~/utils/testUtils'
import { Coords } from '~/lib/models/geolocation'

const DEVICE_CUSTOM_NAMESPACE = '93a04773-2dcf-4cce-a5a8-d2f9328ce497'
export interface DeviceData {
  deviceIdentifier: string
  os?: string
  browser?: string
  brand?: string
  location?: string
}

export const generateDeviceUuid = async () => {
  if (isCypressSpecialBehaviour()) return 'my-awesome-device-userAgent-info'

  const fpPromise = loadFingerprint()

  // Get the visitor identifier when you need it.
  const fp = await fpPromise
  const result = await fp.get()

  return uuidv5(result.visitorId, DEVICE_CUSTOM_NAMESPACE)
}

export const getDeviceId = async () => {
  return await generateDeviceUuid()
}

const isAndroidOrIOS = (str: string | null) => {
  if (!str) return false
  return str.includes('iOS') || str.includes('Android')
}

export const getDeviceName = (device: UserDevice) => {
  if (isAndroidOrIOS(device.os) && !emptyStr(device.model)) return `${device.brand} ${device.model}`

  if (isAndroidOrIOS(device.os) && emptyStr(device.browser)) return device.os

  if (!isAndroidOrIOS(device.os)) return device.os

  return ''
}

export const getDeviceAppName = (device: UserDevice) => {
  if (isAndroidOrIOS(device.os) && emptyStr(device.browser)) return 'App'

  if (isAndroidOrIOS(device.os) && !emptyStr(device.browser)) return device.browser

  if (!isAndroidOrIOS(device.os)) return device.browser

  return ''
}

export const getDeviceInfo = (device: UserDevice) => {
  const name = getDeviceName(device)
  const appName = getDeviceAppName(device)
  const isUnknown = emptyStr(name)
  const icon = isUnknown
    ? {
        src: '/images/icons/profileSections/question.svg',
        alt: 'quesion icon',
      }
    : {
        src: `/images/icons/profileSections/${appName === 'App' ? 'phone' : 'desktop'}.svg`,
        alt: `${appName === 'App' ? 'phone' : 'desktop'} icon`,
      }
  return {
    name,
    icon,
    appName,
    isUnknown,
  }
}

export const getUserAgentUUID = async (userAgent: string, userIP: string) => {
  if (isCypressSpecialBehaviour()) return 'my-awesome-device-userAgent-info'

  let str = userAgent.replace(/\s/, ':')
  if (userIP) str = `${str}+${userIP}`
  return uuidv5(str, DEVICE_CUSTOM_NAMESPACE)
}

export const getDevice = (info: any) => {
  if (isCypressSpecialBehaviour())
    return {
      os: 'Cypress OS',
      browser: 'Cypress',
      brand: 'Cypress Apple',
    }
  if (info.bot)
    return {
      os: info.bot.name || 'Bot/Crawler',
      browser: 'Bot/Crawler',
      brand: 'Bot/Crawler',
    }
  const os = `${info.os?.name ?? ''}${info.os?.version ? ' ' + info.os.version : ''}`
  const browser = `${info.client?.name ?? ''}${
    info.client?.version ? ' ' + info.client.version : ''
  }`
  const brand = info.device?.brand ?? ''
  return {
    os,
    browser,
    brand,
  }
}

export const getCoords = async (): Promise<Coords | undefined> => {
  const permissionStatus = await getPermissionStatus('geolocation')
  if (permissionStatus !== 'granted') return undefined
  try {
    return await getUserLocation()
  } catch (error) {
    return undefined
  }
}

const getValueFromAddressComponents = (
  addressComponents: google.maps.GeocoderAddressComponent[] | undefined,
  key: string,
  property: 'long' | 'short' = 'long'
) => {
  const keyProperty = property === 'long' ? 'long_name' : 'short_name'
  return addressComponents?.find((obj: any) => obj.types.includes(key))?.[keyProperty]
}

const buildCityCountryFromGeocoderResponse = (response: google.maps.GeocoderResult[]): string => {
  const addressComponents = response[0].address_components
  const city =
    getValueFromAddressComponents(addressComponents, 'locality') ??
    getValueFromAddressComponents(addressComponents, 'administrative_area_level_1') ??
    getValueFromAddressComponents(addressComponents, 'administrative_area_level_2') ??
    ''
  const country = getValueFromAddressComponents(addressComponents, 'country') ?? ''
  return `${city}, ${country}`
}

export const geocodeUserLocation = async (): Promise<string | undefined> => {
  if (isCypressSpecialBehaviour()) return 'Ponferrada, España'
  if (!window.google?.maps?.Geocoder) return undefined

  const location = await getCoords()
  if (!location) return undefined

  const geoCoder = new window.google.maps.Geocoder()

  const searchBox: google.maps.GeocoderRequest = {
    location,
  }
  return new Promise(resolve => {
    geoCoder.geocode(searchBox, (response, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK || !response?.length) {
        return resolve(undefined)
      }
      resolve(buildCityCountryFromGeocoderResponse(response))
    })
  })
}

export const getDeviceData = async (): Promise<DeviceData> => {
  const deviceDetector = new DeviceDetector()
  const userAgent = navigator.userAgent
  const info = deviceDetector.parse(userAgent)
  const deviceId = await generateDeviceUuid()
  const location = await geocodeUserLocation()
  return {
    deviceIdentifier: deviceId,
    ...getDevice(info),
    location,
  }
}

export const deviceHeaders = (device: DeviceData): Record<string, string> => {
  const entries: [string, string | undefined][] = [
    ['deviceidentifier', device.deviceIdentifier],
    ['os', device.os],
    ['browser', device.browser],
    ['brand', device.brand],
    ['location', device.location],
  ]
  return Object.fromEntries(
    entries
      .filter((deviceHeader): deviceHeader is [string, string] => Boolean(deviceHeader[1]?.trim()))
      .map(([name, value]) => [name, encodeURIComponent(value)])
  )
}
